'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export function AgbContent() {
  const { t } = useTranslation();
  const p = (key: string) => t(`pages.agb.${key}`);

  return (
    <div className="grid grid-cols-1 gap-5 lg:gap-7.5 mt-5">
      <Card>
        <CardHeader className="flex-col items-start py-5">
          <CardTitle as="h1" className="text-xl">
            {p('title')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{p('subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* §1 Scope */}
          <Section title={p('s1.title')}>
            <BulletList
              items={[p('s1.b1'), p('s1.b2'), p('s1.b3'), p('s1.b4')]}
            />
          </Section>

          {/* §2 Provider */}
          <Section title={p('s2.title')}>
            <p>{p('s2.body')}</p>
            <p>
              Firmendata UG (haftungsbeschränkt)
              <br />
              c/o Ersin Bayraktar
              <br />
              Pistoriusstraße 102A
              <br />
              13086 Berlin
              <br />
              {p('s2.country')}
            </p>
            <p>
              <strong>{p('s2.representatives')}</strong> Julian Lenz, Ersin
              Bayraktar
            </p>
            <p>
              <strong>{p('s2.contact')}</strong>{' '}
              <a
                href="mailto:info@firmendata.com"
                className="text-primary hover:underline"
              >
                info@firmendata.com
              </a>
            </p>
          </Section>

          {/* §3 Registration and Account */}
          <Section title={p('s3.title')}>
            <BulletList
              items={[p('s3.b1'), p('s3.b2'), p('s3.b3'), p('s3.b4')]}
            />
          </Section>

          {/* §4 Services and Scope */}
          <Section title={p('s4.title')}>
            <BulletList
              items={[p('s4.b1'), p('s4.b2'), p('s4.b3'), p('s4.b4')]}
            />
          </Section>

          {/* §5 Subscriptions and Payment */}
          <Section title={p('s5.title')}>
            <BulletList
              items={[
                p('s5.b1'),
                p('s5.b2'),
                p('s5.b3'),
                p('s5.b4'),
                p('s5.b5'),
              ]}
            />
          </Section>

          {/* §6 Right of Withdrawal */}
          <Section title={p('s6.title')}>
            <BulletList items={[p('s6.b1'), p('s6.b2'), p('s6.b3')]} />
          </Section>

          {/* §7 Intellectual Property */}
          <Section title={p('s7.title')}>
            <BulletList items={[p('s7.b1'), p('s7.b2'), p('s7.b3')]} />
          </Section>

          {/* §8 Liability */}
          <Section title={p('s8.title')}>
            <BulletList
              items={[p('s8.b1'), p('s8.b2'), p('s8.b3'), p('s8.b4')]}
            />
          </Section>

          {/* §9 User Obligations */}
          <Section title={p('s9.title')}>
            <BulletList items={[p('s9.b1'), p('s9.b2'), p('s9.b3')]} />
          </Section>

          {/* §10 Termination */}
          <Section title={p('s10.title')}>
            <BulletList items={[p('s10.b1'), p('s10.b2'), p('s10.b3')]} />
          </Section>

          {/* §11 Data Protection */}
          <Section title={p('s11.title')}>
            <p>{p('s11.body')}</p>
          </Section>

          {/* §12 Amendments */}
          <Section title={p('s12.title')}>
            <BulletList items={[p('s12.b1'), p('s12.b2'), p('s12.b3')]} />
          </Section>

          {/* §13 Final Provisions */}
          <Section title={p('s13.title')}>
            <BulletList items={[p('s13.b1'), p('s13.b2'), p('s13.b3')]} />
          </Section>
        </CardContent>
      </Card>
    </div>
  );
}
